import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {onlyItem} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {VisualSortOptionsEnum} from '../../../../store/panels/cells/helpers/cellsPanelSortHelpers';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  inboxSupportsRefinements,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {VisitorCellsHeader} from '../../../visitors/cells/visitorCellsHeader';
import {VisualSortOptionsProps} from './cellsSortOptionsContainer';
import {CellsSortOptionsDropdownItem} from './cellsSortOptionsDropdownItem';

/*
 * Constants.
 */

const largeDividerHeight = 30;

/*
 * Props.
 */

interface CellsSortOptionsProps extends VisualSortOptionsProps {
  resultsCount?: number;
  onSortOptionSelect: HandlerOf<VisualSortOptionsEnum>;
  isUserVisitor?: boolean;
  hasVisitorExperienceV3?: boolean;
  hasP2Refinements: boolean;
  parameters: InboxesParameters;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${largeDividerHeight}px;
  padding: 9px 10px;
  background-color: ${(p) => p.theme.appBackground};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
  height: auto;
  padding: 9px 14px;
  padding-top: 13px;
  padding-bottom: 0;
  border-color: ${(p) => p.theme.appBackground};
  justify-content: flex-start;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${largeDividerHeight}px;
  background-color: ${(p) => p.theme.appBackground};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
  height: auto;
  border-color: ${(p) => p.theme.appBackground};
  justify-content: flex-start;
  margin: 0 8px;
  flex: 0 1 auto;
`;

const StyledSearchResultsWrapperDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`;

const StyledRightSideWrapperDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1;
`;

interface SortOptionStyleProps {
  $isSelected: boolean;
  $hasHover: boolean;
}
const StyledSortOptionDiv = styled.div<SortOptionStyleProps>`
  display: flex;
  align-items: center;
  border-radius: 100px;
  padding: 2px 5px 2px 8px;
  ${(p) => addSortOptionStyles(p)};
`;

const StyledP2RefinementsSortOptionDiv = styled(StyledSortOptionDiv)`
  min-width: 0;
`;

function addSortOptionStyles({$isSelected, $hasHover}: SortOptionStyleProps) {
  if ($isSelected) {
    return css`
      padding: 2px 8px 2px 8px;
      color: ${(p) => p.theme.greys.shade80};
      background: ${(p) => p.theme.alphas.gray10};
    `;
  }

  if ($hasHover) {
    return css`
      padding: 2px 8px 2px 8px;
      &:hover {
        color: ${(p) => p.theme.greys.shade80};
        background: ${(p) => p.theme.alphas.gray10};
      }
    `;
  }

  return '';
}

// We must use `fill:none` to Icon to avoid filling the chevron with `currentColor`.
const StyledChevronIcon = styled(Icon)`
  margin-left: 5px;
  fill: none;
`;

const StyledP2RefinementsChevronIcon = styled(StyledChevronIcon)`
  flex-shrink: 0;
`;

const StyledSortIcon = styled(Icon)`
  margin-right: 4px;
`;

const StyledP2RefinementsSortIcon = styled(StyledSortIcon)`
  flex-shrink: 0;
`;

const StyledP2RefinementsSelectedSortOptionSpan = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledResultsSpan = styled.span`
  margin-left: 4px;
  margin-top: 1px;
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.medium};
`;

const StyledP2RefinementsResultsSpan = styled(StyledResultsSpan)`
  font-size: ${fontSizes.verySmall};
  margin-left: 14px;
`;

/*
 * Component.
 */

export const CellsSortOptions: FC<CellsSortOptionsProps> = (props) => {
  const {hasP2Refinements, parameters} = props;

  if (props.isUserVisitor) {
    return <VisitorCellsHeader hasVisitorExperienceV3={props.hasVisitorExperienceV3} />;
  }

  if (props.selectedSortOption === onlyItem(props.sortOptions)) {
    return <StyledWrapperDiv>{renderTitle(false, props, false)}</StyledWrapperDiv>;
  }

  if (hasP2Refinements && hasSearchInInboxesParameters(parameters)) {
    return (
      <StyledSearchResultsWrapperDiv>
        <StyledP2RefinementsResultsSpan>{maybeRenderResultsCount(props)}</StyledP2RefinementsResultsSpan>
        <StyledPopoverCoordinator
          renderButton={(isOpen) => renderTitle(isOpen, props, true)}
          renderModal={(modalProps) => renderDropdown(modalProps, props)}
        />
      </StyledSearchResultsWrapperDiv>
    );
  }

  if (hasP2Refinements && inboxSupportsRefinements(parameters)) {
    return (
      <StyledPopoverCoordinator
        renderButton={(isOpen) => renderTitle(isOpen, props, true)}
        renderModal={(modalProps) => renderDropdown(modalProps, props)}
      />
    );
  }

  if (hasP2Refinements) {
    return (
      <StyledRightSideWrapperDiv>
        <StyledPopoverCoordinator
          renderButton={(isOpen) => renderTitle(isOpen, props, true)}
          renderModal={(modalProps) => renderDropdown(modalProps, props)}
        />
      </StyledRightSideWrapperDiv>
    );
  }

  return (
    <StyledWrapperDiv>
      <PopoverCoordinator
        renderButton={(isOpen) => renderTitle(isOpen, props, true)}
        renderModal={(modalProps) => renderDropdown(modalProps, props)}
      />
      <StyledResultsSpan>{maybeRenderResultsCount(props)}</StyledResultsSpan>
    </StyledWrapperDiv>
  );
};

function renderTitle(isOpen: boolean, props: CellsSortOptionsProps, shouldShowChevron: boolean) {
  const {hasP2Refinements} = props;
  const selectedSortOption = props.selectedSortOption || VisualSortOptionsEnum.NEWEST;

  if (hasP2Refinements) {
    return (
      <StyledP2RefinementsSortOptionDiv $isSelected={isOpen} $hasHover={shouldShowChevron}>
        <StyledP2RefinementsSortIcon name="sortSmall" />
        <StyledP2RefinementsSelectedSortOptionSpan>
          {sortOptionTitles[selectedSortOption]}
        </StyledP2RefinementsSelectedSortOptionSpan>
        {shouldShowChevron && <StyledP2RefinementsChevronIcon name="chevronDownTiny" />}
      </StyledP2RefinementsSortOptionDiv>
    );
  }

  return (
    <StyledSortOptionDiv $isSelected={isOpen} $hasHover={shouldShowChevron}>
      <StyledSortIcon name="sortSmall" />
      {sortOptionTitles[selectedSortOption]}
      {shouldShowChevron && <StyledChevronIcon name="chevronDownTiny" />}
    </StyledSortOptionDiv>
  );
}

function renderDropdown(modalProps: PopoverTransferProps, props: CellsSortOptionsProps) {
  const {sortOptions, selectedSortOption, onSortOptionSelect} = props;
  const dropdownItemRenders = sortOptions.map((sortOption) => (
    <CellsSortOptionsDropdownItem
      key={sortOption}
      isChecked={sortOption === selectedSortOption}
      onSelect={() => onSortOptionSelect(sortOption)}
    >
      {sortOptionTitles[sortOption]}
    </CellsSortOptionsDropdownItem>
  ));

  return <SimpleDropdown {...modalProps}>{dropdownItemRenders}</SimpleDropdown>;
}

function maybeRenderResultsCount(props: CellsSortOptionsProps) {
  const {resultsCount} = props;

  if (!resultsCount || resultsCount === 0) {
    return null;
  }

  if (resultsCount > 99) {
    return <Trans id="cells-sort-options-results-99">99+ results</Trans>;
  }

  return (
    <Plural id="cells-sort-options-results-count" value={resultsCount} one="1 result" other="# results" />
  );
}

/*
 * Variants.
 */

type SortOptionTitles = {[T in VisualSortOptionsEnum]: ReactNode};

export const sortOptionTitles: SortOptionTitles = {
  [VisualSortOptionsEnum.NEWEST]: <Trans id="cells-sort-newest">Newest</Trans>,
  [VisualSortOptionsEnum.OLDEST]: <Trans id="cells-sort-oldest">Oldest</Trans>,
  [VisualSortOptionsEnum.NEWEST_UNREPLIED]: <Trans id="cells-sort-newestunreplied">Newest Unreplied</Trans>,
  [VisualSortOptionsEnum.OLDEST_UNREPLIED]: <Trans id="cells-sort-oldestunreplied">Oldest Unreplied</Trans>,
  [VisualSortOptionsEnum.DUE_DATE]: <Trans id="cells-sort-duedate">Due Date</Trans>,
};
